import { maxBy } from 'lodash';
import * as Api from '../lib/typescript-axios';

import { AssessmentClient } from '../lib/assessment-client';
import { BadgeManager } from '../lib/badge-manager';
import { HesMutex } from '../lib/mutex';
import { AssessmentInvalidStateError, ReportBlockedError } from '../lib/errors';
import { ReportClient } from '../lib/report-client';
import { AssessmentTypeIri } from './assessment-type-iri';
import { getIdFromIri } from './utils';

export enum REPORT_STATUS {
  REPORT = 'Report',
  PARENT_REPORT = 'ParentReport',
}

export function isReportStatus(value: string): value is REPORT_STATUS {
  return value === REPORT_STATUS.REPORT || value === REPORT_STATUS.PARENT_REPORT;
}

export interface CareerWages {
  average?: string | null | undefined;
  ninetiethPercentile: string | null | undefined;
  seventyFifthPercentile: string | null | undefined;
  fiftiethPercentile: string | null | undefined;
  twentyFifthPercentile: string | null | undefined;
  tenthPercentile: string | null | undefined;
}
export interface CareerWageArea {
  areaId: string;
  areaName: string;
  areaType: string;
  wagesAnnually: CareerWages;
  wagesHourly: CareerWages;
  childAreas: Array<CareerWageArea>;
}

export interface CompareToMeScores {
  skillsScores: Array<Api.AssessmentMacroScoreMinimumRead> | undefined;
  intelligencesScores: Array<Api.AssessmentScoreMinimumRead> | undefined;
  aptitudesScores: Array<Api.AssessmentMacroScoreMinimumRead> | undefined;
}

// The career matches contributed by ONE assessment, plus the id of the assessment they came from so
// consumers can resolve its completion date / product configuration without repeating the
// "newest completed assessment per type" selection that produced it.
export interface CareerMatchesByAssessmentTypeItem {
  assessmentId: string;
  careerMatches: Array<Api.CareerMatchFullRead>;
}

// Career aptitude baselines arrive on a 0-1 scale; normalize to 0-100 to match the user aptitude
// scores and the skills/intelligences baselines before they reach the compare graphs / overall average.
export function normalizeCareerAptitudeBaselines(careerItem: Api.CareerItemFullRead): Api.CareerItemFullRead {
  const scale = (baselines: Api.CareerItemFullRead['careerAptitudesBaselines']) =>
    baselines.map((baseline) => ({ ...baseline, value: baseline.value * 100 }));

  if (!careerItem.careerAptitudesBaselines) {
    return careerItem;
  }

  return {
    ...careerItem,
    careerAptitudesBaselines: scale(careerItem.careerAptitudesBaselines),
    // Embedded related careers carry their own 0-1 baselines; scale them at the same chokepoint so
    // every CareerItem this method returns is uniformly 0-100, including any read off relatedCareers.
    relatedCareers: careerItem.relatedCareers
      ? careerItem.relatedCareers.map((related) =>
          related.careerAptitudesBaselines ? { ...related, careerAptitudesBaselines: scale(related.careerAptitudesBaselines) } : related,
        )
      : careerItem.relatedCareers,
  };
}

export class ReportEngine {
  /**
   *  status
   *
   * @type REPORT_STATUS
   * @memberof ReportEngine
   */
  protected status: REPORT_STATUS;

  /**
   *  userId
   *
   * @type string
   * @memberof ReportEngine
   */
  protected userId: string;

  /**
   *  mutex
   *
   * @type HesMutex
   * @memberof ReportEngine
   */
  protected mutex: HesMutex;

  /**
   *  allCareersMutex
   *
   * @type HesMutex
   * @memberof ReportEngine
   */
  protected allCareersMutex: HesMutex;

  /**
   *  productConfigurationsMutex
   *
   * @type HesMutex
   * @memberof ReportEngine
   */
  protected productConfigurationsMutex: HesMutex;

  /**
   *  assessmentMinimumReadsMutex
   *
   * Not constructor-injected like the others: nothing needs it before construction, unlike the
   * mutex initPropertyValues takes.
   *
   * @type HesMutex
   * @memberof ReportEngine
   */
  protected assessmentMinimumReadsMutex: HesMutex = new HesMutex();

  /**
   *  assessmentCareerMatchesByAssessmentMutex
   *
   * Guards getAssessmentCareerMatchesByAssessment's check-then-fetch the same way
   * assessmentMinimumReadsMutex guards getAssessmentMinimumReads: concurrent callers (e.g. the
   * career profile dialog fetching match scores for several related careers in parallel) would
   * otherwise race past the unguarded size check and issue duplicate requests on a cold engine.
   *
   * @type HesMutex
   * @memberof ReportEngine
   */
  protected assessmentCareerMatchesByAssessmentMutex: HesMutex = new HesMutex();

  /**
   *  ReportClient object
   *
   * @type ReportClient
   * @memberof ReportEngine
   */
  protected reportClient: ReportClient;

  /**
   *  AssessmentClient object
   *
   * @type AssessmentClient
   * @memberof ReportEngine
   */
  protected assessmentClient: AssessmentClient;

  /**
   *  assessmentTypeIri
   *
   * @type string
   * @memberof ReportEngine
   */
  protected assessmentTypeIri: string;

  /**
   *  achieveWorksReportTypeId
   *
   * @type string
   * @memberof ReportEngine
   */
  protected achieveWorksReportTypeId: string;

  /**
   *  achieveWorksReport
   *
   * @type Api.AchieveWorksReportFullRead
   * @memberof ReportEngine
   */
  protected achieveWorksReport: Api.AchieveWorksReportFullRead;

  /**
   *  reportUserAnswers
   *
   * @type Array<Api.ReportUserAnswerFullRead>
   * @memberof ReportEngine
   */
  protected reportUserAnswers: Map<string, Api.ReportUserAnswerFullRead>;

  /**
   *  badgeManager
   *
   * @type BadgeManager
   * @memberof ReportEngine
   */
  protected badgeManager: BadgeManager | null;

  /**
   *  reportRatings
   *
   * @type Array<Api.AchieveWorksReportRatingFullRead>
   * @memberof ReportEngine
   */
  protected reportRatings: Array<Api.AchieveWorksReportRatingFullRead>;

  /**
   *  reportElementRatings
   *
   * @type Map<string, Api.ReportElementRatingFullRead>
   * @memberof ReportEngine
   */
  protected reportElementRatings: Map<string, Api.ReportElementRatingFullRead>;

  /**
   *  reportCareers
   *
   * @type Array<Api.CareerFullRead>
   * @memberof ReportEngine
   */
  protected reportCareers: Array<Api.CareerFullRead> = new Array<Api.CareerFullRead>();

  /**
   *  allCareers
   *
   * @type Array<Api.CareerFullRead>
   * @memberof ReportEngine
   */
  protected allCareers: Array<Api.CareerMinimumRead> = new Array<Api.CareerMinimumRead>();

  /**
   *  assessmentMinimumReads
   *
   * @type Array<Api.CareerFullRead>
   * @memberof ReportEngine
   */
  protected assessmentMinimumReads: Array<Api.AssessmentMinimumRead> = new Array<Api.AssessmentMinimumRead>();

  /**
   *  assessmentCareerMatches
   *
   * @type Array<Api.AssessmentCareerMatchesFullRead>
   * @memberof ReportEngine
   */
  protected assessmentCareerMatchesByAssessment: Map<string, Api.AssessmentCareerMatchesFullRead> = new Map<
    string,
    Api.AssessmentCareerMatchesFullRead
  >();

  /**
   *  productConfigurations, keyed by product configuration id
   *
   * @type Map<string, Api.ProductConfigurationFullRead>
   * @memberof ReportEngine
   */
  protected productConfigurationsById: Map<string, Api.ProductConfigurationFullRead> = new Map<string, Api.ProductConfigurationFullRead>();

  /**
   *  assessmentRatingAnswers
   *
   * @type Array<Api.AnswerMinimumRead>
   * @memberof ReportEngine
   */
  protected assessmentRatingAnswers: Array<Api.AnswerMinimumRead> = new Array<Api.AnswerMinimumRead>();

  /**
   *  combinedCareers
   *
   * @type Array<Api.CareerFullRead>
   * @memberof ReportEngine
   */
  protected combinedCareers: Array<Api.CareerMinimumRead> = new Array<Api.CareerMinimumRead>();

  /**
   *  careerUserRatings
   *
   * @type Map<string, Api.CareerUserRatingFullRead>
   * @memberof ReportEngine
   */
  protected careerUserRatings: Map<string, Api.CareerUserRatingFullRead> = new Map<string, Api.CareerUserRatingFullRead>();

  /**
   *  compareToMeScores
   *
   * @type CompareToMeScores
   * @memberof ReportEngine
   */
  protected compareToMeScores: CompareToMeScores | undefined;

  private static async initPropertyValues(
    reportClient: ReportClient,
    achieveWorksReportId: string,
    hasCareers: boolean,
    hasBadges: boolean,
    mutex: HesMutex,
  ): Promise<{
    achieveWorksReport: Api.AchieveWorksReportFullRead;
    reportUserAnswers: Map<string, Api.ReportUserAnswerFullRead>;
    badgeManager: BadgeManager | null;
    reportRatings: Array<Api.AchieveWorksReportRatingFullRead>;
    reportElementRatings: Map<string, Api.ReportElementRatingFullRead>;
    reportCareers: Array<Api.CareerFullRead>;
    careerUserRatings: Map<string, Api.CareerUserRatingFullRead>;
    assessmentRatingAnswers: Array<Api.AnswerMinimumRead>;
  }> {
    const reportEnginePromises: Array<
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      Promise<any> | null | Array<Api.CareerFullRead> | Map<string, Api.CareerUserRatingFullRead> | Array<Api.AnswerMinimumRead>
    > = [];

    const achieveWorksReport = await reportClient.getReport(achieveWorksReportId);

    reportEnginePromises.push(
      reportClient.getReportUserAnswers(achieveWorksReportId),
      reportClient.getReportRatings(
        achieveWorksReport.productConfiguration.assessmentType === AssessmentTypeIri.TRUTALENTS_ASSESSMENT_TYPE_IRI,
      ),
      reportClient.getReportElementRatings(achieveWorksReportId),
    );

    if (hasBadges) {
      reportEnginePromises.push(
        BadgeManager.getBadgeManager(
          getIdFromIri(achieveWorksReport.productConfiguration.achieveWorksReportType),
          achieveWorksReport.id,
          reportClient,
          mutex,
        ),
      );
    } else {
      reportEnginePromises.push(null);
    }

    if (hasCareers) {
      reportEnginePromises.push(reportClient.getCareersForReport(achieveWorksReportId), reportClient.getCareerUserRatings());
    } else {
      reportEnginePromises.push(new Array<Api.CareerFullRead>(), new Map<string, Api.CareerUserRatingFullRead>());
    }

    if (achieveWorksReport.assessmentRating) {
      reportEnginePromises.push(reportClient.getAnswersForQuestion(achieveWorksReport.assessmentRating.answer.question.id));
    } else {
      reportEnginePromises.push(new Array<Api.AnswerMinimumRead>());
    }

    // eslint-disable-next-line prefer-const
    const [
      reportUserAnswers,
      reportRatings,
      reportElementRatings,
      badgeManager,
      reportCareers,
      careerUserRatings,
      assessmentRatingAnswers,
    ] = await Promise.all(reportEnginePromises);

    return {
      achieveWorksReport,
      reportUserAnswers,
      badgeManager,
      reportRatings,
      reportElementRatings,
      reportCareers,
      careerUserRatings,
      assessmentRatingAnswers,
    };
  }

  public static async getReportEngine(
    basePath: string,
    userId: string,
    status: string,
    token: string,
    achieveWorksReportId: string,
    hasCareers: boolean,
    hasBadges: boolean,
    enableReportBlocking: boolean,
    isResultReviewed: boolean,
    locale?: string,
  ): Promise<ReportEngine> {
    const reportClient = new ReportClient(basePath, token, locale);
    const assessmentClient = new AssessmentClient(basePath, token, locale);

    if (!isReportStatus(status)) {
      throw new AssessmentInvalidStateError('invalid state: ' + status);
    }

    if (enableReportBlocking && !isResultReviewed) {
      throw new ReportBlockedError();
    }

    const mutex = new HesMutex();
    const allCareersMutex = new HesMutex();
    const productConfigurationsMutex = new HesMutex();
    const propertyValues = await ReportEngine.initPropertyValues(reportClient, achieveWorksReportId, hasCareers, hasBadges, mutex);

    return new ReportEngine(
      userId,
      status,
      reportClient,
      assessmentClient,
      propertyValues.achieveWorksReport,
      propertyValues.reportUserAnswers,
      propertyValues.badgeManager,
      propertyValues.reportRatings,
      propertyValues.reportElementRatings,
      propertyValues.reportCareers,
      propertyValues.careerUserRatings,
      propertyValues.assessmentRatingAnswers,
      mutex,
      allCareersMutex,
      productConfigurationsMutex,
    );
  }

  public constructor(
    userId: string,
    status: REPORT_STATUS,
    reportClient: ReportClient,
    assessmentClient: AssessmentClient,
    report: Api.AchieveWorksReportFullRead,
    reportUserAnswers: Map<string, Api.ReportUserAnswerFullRead>,
    badgeManager: BadgeManager | null,
    reportRatings: Array<Api.AchieveWorksReportRatingFullRead>,
    reportElementRatings: Map<string, Api.ReportElementRatingFullRead>,
    reportCareers: Array<Api.CareerFullRead>,
    careerUserRatings: Map<string, Api.CareerUserRatingFullRead>,
    assessmentRatingAnswers: Array<Api.AnswerMinimumRead>,
    mutex: HesMutex,
    allCareersMutex: HesMutex,
    productConfigurationsMutex: HesMutex,
  ) {
    this.userId = userId;
    this.status = status;
    this.reportClient = reportClient;
    this.assessmentClient = assessmentClient;
    this.assessmentTypeIri = report.productConfiguration.assessmentType;
    this.achieveWorksReportTypeId = report.productConfiguration.achieveWorksReportType;
    this.achieveWorksReport = report;
    this.reportUserAnswers = reportUserAnswers;
    this.badgeManager = badgeManager;
    this.reportRatings = reportRatings;
    this.reportElementRatings = reportElementRatings;
    this.reportCareers = reportCareers;
    this.careerUserRatings = careerUserRatings;
    this.assessmentRatingAnswers = assessmentRatingAnswers.reverse();
    this.mutex = mutex;
    this.allCareersMutex = allCareersMutex;
    this.productConfigurationsMutex = productConfigurationsMutex;
    this.normalizeScores();
    this.initializeRatingAnswers();
  }

  private normalizeScores(): void {
    if (this.assessmentTypeIri === AssessmentTypeIri.PERSONALITY_ASSESSMENT_TYPE_IRI) {
      const oldRange = 2;
      const newRange = 100;

      this.achieveWorksReport.assessmentScores.forEach((achieveWorksReportAssessmentScoreFullRead, index) => {
        this.achieveWorksReport.assessmentScores[index].score =
          ((achieveWorksReportAssessmentScoreFullRead.score + 1) * newRange) / oldRange;
      });
    }
  }

  private initializeRatingAnswers(): void {
    if (this.achieveWorksReport.assessmentRating) {
      this.achieveWorksReport.assessmentRating.answer.question.answers = this.assessmentRatingAnswers;
    }
  }

  public getUserId(): string {
    return this.userId;
  }

  public getLocale(): string {
    return this.reportClient.getLocale();
  }

  public getAchieveWorksReport(): Api.AchieveWorksReportFullRead {
    return this.achieveWorksReport;
  }

  public changeLanguage($language: string): Promise<Api.AchieveWorksReportFullRead> {
    this.reportClient.setLocale($language);

    return this.reInitialize();
  }

  public async reInitialize(): Promise<Api.AchieveWorksReportFullRead> {
    const propertyValues = await ReportEngine.initPropertyValues(
      this.reportClient,
      this.achieveWorksReport.id,
      this.reportCareers.length > 0,
      this.badgeManager !== null,
      new HesMutex(),
    );

    this.achieveWorksReport = propertyValues.achieveWorksReport;
    this.reportUserAnswers = propertyValues.reportUserAnswers;
    this.badgeManager = propertyValues.badgeManager;
    this.reportRatings = propertyValues.reportRatings;
    this.reportElementRatings = propertyValues.reportElementRatings;
    this.reportCareers = propertyValues.reportCareers;
    this.careerUserRatings = propertyValues.careerUserRatings;
    this.assessmentRatingAnswers = propertyValues.assessmentRatingAnswers;

    // Product names are resolved per request from the brand translation via Accept-Language, but the
    // cache is keyed by product configuration id alone. changeLanguage is this method's only caller,
    // so dropping the cache here is what keeps names from serving in the previous locale. Inside the
    // mutex because CareerList warms the cache unawaited: a clear that lands mid-fetch would be
    // undone by that fetch resolving with previous-locale names.
    await this.productConfigurationsMutex.use(async () => {
      this.productConfigurationsById.clear();
    });

    this.normalizeScores();
    this.initializeRatingAnswers();

    return this.achieveWorksReport;
  }

  public getReport(): Api.AchieveWorksReportFullRead {
    return this.achieveWorksReport;
  }

  public getReportRatings(): Array<Api.AchieveWorksReportRatingFullRead> {
    return this.reportRatings;
  }

  public getCareerRange(): Api.CareerRangeMinimumRead | null {
    return this.achieveWorksReport.careerRange || null;
  }

  public getMinOnetJobZone(): number | null {
    return this.achieveWorksReport.minOnetJobZone ?? null;
  }

  public getReportElementRatings(): Map<string, Api.ReportElementRatingFullRead> {
    return this.reportElementRatings;
  }

  public getBadgeManager(): BadgeManager | null {
    return this.badgeManager;
  }

  public getReportCareers(): Api.CareerFullRead[] {
    return this.reportCareers;
  }

  // Single-flighted: the career profile dialog awaits two callers of this in one Promise.all, so an
  // unguarded check-then-fetch would issue the same GET /api/assessments twice on a cold engine.
  public async getAssessmentMinimumReads(): Promise<Array<Api.AssessmentMinimumRead>> {
    return this.assessmentMinimumReadsMutex.use(async () => {
      if (this.assessmentMinimumReads.length === 0) {
        this.assessmentMinimumReads = await this.assessmentClient.getAssessmentMinimumReads();
      }

      return this.assessmentMinimumReads;
    });
  }

  // Keyed by PRODUCT CONFIGURATION id, so callers resolve one for an assessment through the
  // assessment's own productConfiguration iri. Only the configurations the user's assessments
  // reference are fetched - a handful, not the whole collection - and the career section warms them
  // while the report renders, see CareerList.
  public async getProductConfigurationsForAssessments(): Promise<Map<string, Api.ProductConfigurationFullRead>> {
    const assessmentMinimumReads = await this.getAssessmentMinimumReads();
    const productConfigurationIds = [
      ...new Set(
        assessmentMinimumReads.map((assessmentMinimumRead: Api.AssessmentMinimumRead) =>
          getIdFromIri(assessmentMinimumRead.productConfiguration),
        ),
      ),
    ];

    return this.getProductConfigurationsByIds(productConfigurationIds);
  }

  // Fetches only the ids missing from the cache, so a warmed cache costs no request and a second
  // caller asking for a subset costs nothing either.
  //
  // Returns the WHOLE cache, not just the requested ids - `ids` says what to ensure is loaded, not
  // what to return. Callers resolve by keyed `.get()`, and handing back the shared map keeps its
  // identity stable across calls (the specs pin that with `toBe`). Do not iterate the result
  // expecting only what you asked for.
  public async getProductConfigurationsByIds(ids: Array<string>): Promise<Map<string, Api.ProductConfigurationFullRead>> {
    return this.productConfigurationsMutex.use(async () => {
      const missingIds = ids.filter((id: string) => !this.productConfigurationsById.has(id));

      if (missingIds.length > 0) {
        const productConfigurations = await this.reportClient.getProductConfigurationsByIds(missingIds);

        productConfigurations.forEach((productConfiguration: Api.ProductConfigurationFullRead) => {
          this.productConfigurationsById.set(productConfiguration.id, productConfiguration);
        });
      }

      return this.productConfigurationsById;
    });
  }

  public async getCareerProfile(careerId: string): Promise<Api.CareerItemFullRead> {
    return normalizeCareerAptitudeBaselines(await this.reportClient.getCareerProfile(careerId));
  }

  public async getCareerWages(careerId: string): Promise<CareerWageArea | null> {
    const careerWages = await this.reportClient.getCareerWages(careerId);

    const nationCareerWages = careerWages.find((careerWageFullRead) => careerWageFullRead.parentAreaId === null);

    if (nationCareerWages) {
      return ReportEngine.getCareerWageArea(nationCareerWages, careerWages);
    }

    return null;
  }

  private static getCareerWageArea(
    parentCareerWageFullRead: Api.CareerWageFullRead,
    careerWages: Array<Api.CareerWageFullRead>,
  ): CareerWageArea {
    const childWageAreas = [] as Array<CareerWageArea>;

    careerWages.forEach((careerWageFullRead) => {
      if (parentCareerWageFullRead.areaType !== 'metro' && careerWageFullRead.parentAreaId === parentCareerWageFullRead.areaId) {
        childWageAreas.push(ReportEngine.getCareerWageArea(careerWageFullRead, careerWages));
      }
    });

    return {
      areaId: parentCareerWageFullRead.areaId,
      areaName: parentCareerWageFullRead.areaName,
      areaType: parentCareerWageFullRead.areaType,
      wagesAnnually: {
        average: parentCareerWageFullRead.meanAverageWage,
        ninetiethPercentile: parentCareerWageFullRead.annualWage90thPercentile,
        seventyFifthPercentile: parentCareerWageFullRead.annualWage75thPercentile,
        fiftiethPercentile: parentCareerWageFullRead.annualWage50thPercentile,
        twentyFifthPercentile: parentCareerWageFullRead.annualWage25thPercentile,
        tenthPercentile: parentCareerWageFullRead.annualWage10thPercentile,
      },
      wagesHourly: {
        average: parentCareerWageFullRead.meanHourlyWage,
        ninetiethPercentile: parentCareerWageFullRead.hourlyWage90thPercentile,
        seventyFifthPercentile: parentCareerWageFullRead.hourlyWage75thPercentile,
        fiftiethPercentile: parentCareerWageFullRead.hourlyWage50thPercentile,
        twentyFifthPercentile: parentCareerWageFullRead.hourlyWage25thPercentile,
        tenthPercentile: parentCareerWageFullRead.hourlyWage10thPercentile,
      },
      childAreas: childWageAreas,
    };
  }

  public async getAllCareers(): Promise<Array<Api.CareerMinimumRead>> {
    return this.allCareersMutex.use(async () => {
      if (this.allCareers.length === 0) {
        this.allCareers = await this.reportClient.getAllCareers();
      }

      return this.allCareers;
    });
  }

  public getCareerUserRating(careerId: string): Api.CareerUserRatingFullRead | null {
    const careerUserRating = this.careerUserRatings.get(careerId);
    if (careerUserRating) {
      return careerUserRating;
    }

    return null;
  }

  public getCareerUserRatings(): Map<string, Api.CareerUserRatingFullRead> {
    return this.careerUserRatings;
  }

  public async deleteCareerUserRating(careerId: string): Promise<void> {
    const careerUserRating = this.careerUserRatings.get(careerId);

    if (careerUserRating) {
      await this.reportClient.deleteCareerUserRating(careerUserRating.id);
      this.careerUserRatings.delete(careerId);
    }
  }

  public async createCareerUserRating(careerId: string, ratingId: string): Promise<Api.CareerUserRatingFullRead> {
    return this.mutex.use(async () => {
      const careerUserRatingFullRead = this.careerUserRatings.get(careerId);
      const newCareerUserRatingFullRead = await (careerUserRatingFullRead
        ? this.reportClient.updateCareerUserRating(careerUserRatingFullRead, ratingId)
        : this.reportClient.createCareerUserRating(this.userId, careerId, ratingId));

      this.careerUserRatings.set(newCareerUserRatingFullRead.career.id, newCareerUserRatingFullRead);

      return newCareerUserRatingFullRead;
    });
  }

  public async answerReportQuestion(questionId: string, answerIds: Array<string>): Promise<void> {
    await this.mutex.use(async () => {
      let reportUserAnswerFullRead;

      if (this.reportUserAnswers !== undefined) {
        reportUserAnswerFullRead = this.reportUserAnswers.get(questionId);

        reportUserAnswerFullRead = await (reportUserAnswerFullRead === undefined
          ? this.reportClient.answerReportQuestion(this.achieveWorksReport.id, questionId, answerIds)
          : this.reportClient.updateReportQuestionAnswer(reportUserAnswerFullRead.id, this.achieveWorksReport.id, questionId, answerIds));

        this.reportUserAnswers.set(reportUserAnswerFullRead.achieveWorksReportQuestion.id, reportUserAnswerFullRead);
      }
    });
  }

  public async deleteReportElementRating(elementId: string): Promise<void> {
    await this.mutex.use(async () => {
      const reportElementRating = this.reportElementRatings.get(elementId);

      if (reportElementRating) {
        await this.reportClient.deleteReportElementRating(reportElementRating.id);
        this.reportElementRatings.delete(elementId);
      }
    });
  }

  public async rateReportElement(elementId: string, ratingId: string): Promise<Api.ReportElementRatingFullRead> {
    return this.mutex.use(async () => {
      let reportElementRatingFullRead;

      reportElementRatingFullRead = this.reportElementRatings.get(elementId);

      reportElementRatingFullRead = await (reportElementRatingFullRead === undefined
        ? this.reportClient.rateReportElement(this.achieveWorksReport.id, elementId, ratingId)
        : this.reportClient.updateReportElementRating(reportElementRatingFullRead.id, this.achieveWorksReport.id, elementId, ratingId));

      this.reportElementRatings.set(getIdFromIri(reportElementRatingFullRead.achieveWorksReportElement), reportElementRatingFullRead);

      return reportElementRatingFullRead;
    });
  }

  public getReportUserAnswer(questionId: string): Api.ReportUserAnswerFullRead | null {
    const reportUserAnswerFullRead = this.reportUserAnswers.get(questionId);

    if (reportUserAnswerFullRead !== undefined) {
      return reportUserAnswerFullRead;
    }

    return null;
  }

  public getReportElementRating(elementId: string): Api.ReportElementRatingFullRead | null {
    const reportElementRatingFullRead = this.reportElementRatings.get(elementId);
    if (reportElementRatingFullRead !== undefined) {
      return reportElementRatingFullRead;
    }

    return null;
  }

  protected async getAssessmentCareerMatchesByAssessment(): Promise<Map<string, Api.AssessmentCareerMatchesFullRead>> {
    return this.assessmentCareerMatchesByAssessmentMutex.use(async () => {
      if (this.assessmentCareerMatchesByAssessment.size === 0) {
        this.assessmentCareerMatchesByAssessment = await this.reportClient.getAssessmentCareerMatchesByAssessment();
      }

      return this.assessmentCareerMatchesByAssessment;
    });
  }

  // careers in range 1 cannot be used in combined careers
  private isCareerInCareerRange1(careerIri: string): boolean {
    const maxIdCareerRange1 = 999;
    const careerId = getIdFromIri(careerIri);

    return parseInt(careerId, 10) <= maxIdCareerRange1;
  }

  // Keyed by assessment TYPE iri: one entry per newest report-status assessment of each type that
  // has career matches, so a retaken assessment contributes once rather than twice.
  public async getCareerMatchesByAssessmentTypeForReport(): Promise<Map<string, CareerMatchesByAssessmentTypeItem>> {
    return this.getCareerMatchesByAssessmentType(this.getAssessmentId(), this.assessmentTypeIri);
  }

  // reportAssessmentId is a raw id, not an iri: it is a key into assessmentCareerMatchesByAssessment,
  // which ReportClient builds with getRawId.
  protected async getCareerMatchesByAssessmentType(
    reportAssessmentId?: string,
    reportAssessmentTypeIri?: string,
  ): Promise<Map<string, CareerMatchesByAssessmentTypeItem>> {
    const careerMatchesByAssessmentType: Map<string, CareerMatchesByAssessmentTypeItem> = new Map();
    const assessmentCareerMatchesByAssessment = await this.getAssessmentCareerMatchesByAssessment();
    const assessmentMinimumReads = await this.getAssessmentMinimumReads();

    [
      AssessmentTypeIri.PERSONALITY_ASSESSMENT_TYPE_IRI,
      AssessmentTypeIri.SKILLS_ASSESSMENT_TYPE_IRI,
      AssessmentTypeIri.INTELLIGENCES_ASSESSMENT_TYPE_IRI,
    ].forEach((assessmentTypeIri: string) => {
      const latestReportStatusAssessment = ReportEngine.findLatestReportStatusAssessment(assessmentTypeIri, assessmentMinimumReads);

      if (latestReportStatusAssessment) {
        this.addCareerMatchesForAssessment(
          careerMatchesByAssessmentType,
          assessmentCareerMatchesByAssessment,
          assessmentTypeIri,
          latestReportStatusAssessment.id,
        );
      }
    });

    // Aptitudes (AWA3): the parent assessment's completedAt is only set once ALL sections are
    // completed, but aptitude career matches should join the combined blend as soon as the parent
    // has been scored. Gate on it carrying a macro score - the same moment its career matches are
    // created - then look up those matches by the parent aptitude assessment id.
    const aptitudeParent = ReportEngine.findAptitudeParentWithMacroScores(assessmentMinimumReads);

    if (aptitudeParent) {
      this.addCareerMatchesForAssessment(
        careerMatchesByAssessmentType,
        assessmentCareerMatchesByAssessment,
        AssessmentTypeIri.APTITUDES_ASSESSMENT_TYPE_IRI,
        aptitudeParent.id,
      );
    }

    // Runs last so the report's own assessment wins the type key it shares with the newest
    // report-status assessment of that type.
    if (reportAssessmentId && reportAssessmentTypeIri) {
      this.addCareerMatchesForAssessment(
        careerMatchesByAssessmentType,
        assessmentCareerMatchesByAssessment,
        reportAssessmentTypeIri,
        reportAssessmentId,
      );
    }

    return careerMatchesByAssessmentType;
  }

  private addCareerMatchesForAssessment(
    careerMatchesByAssessmentType: Map<string, CareerMatchesByAssessmentTypeItem>,
    assessmentCareerMatchesByAssessment: Map<string, Api.AssessmentCareerMatchesFullRead>,
    assessmentTypeIri: string,
    assessmentId: string,
  ): void {
    const assessmentCareerMatches = assessmentCareerMatchesByAssessment.get(assessmentId);

    if (
      assessmentCareerMatches &&
      assessmentCareerMatches.careerMatches.length > 0 &&
      !this.isCareerInCareerRange1(assessmentCareerMatches.careerMatches[0].career)
    ) {
      careerMatchesByAssessmentType.set(assessmentTypeIri, { assessmentId, careerMatches: assessmentCareerMatches.careerMatches });
    }
  }

  public getAssessmentId(): string {
    return ReportClient.getRawId(this.achieveWorksReport.assessment);
  }

  public async getCombinedCareersForReport(): Promise<Array<Api.CareerMinimumRead> | undefined> {
    return this.getCombinedCareers(this.getAssessmentId(), this.assessmentTypeIri);
  }

  public async getCombinedCareers(assessmentId?: string, assessmentTypeIri?: string): Promise<Array<Api.CareerMinimumRead> | undefined> {
    if (this.combinedCareers.length === 0) {
      const careerMatchesByAssessmentType = await this.getCareerMatchesByAssessmentType(assessmentId, assessmentTypeIri);

      if (careerMatchesByAssessmentType.size === 1) {
        return;
      }

      const allCareers = await this.getAllCareers();
      const careerScores: Map<string, number> = new Map();

      careerMatchesByAssessmentType.forEach(({ careerMatches }) => {
        careerMatches.forEach((careerMatch: Api.CareerMatchFullRead) => {
          const careerScore = careerScores.get(careerMatch.career);
          const newCareerScore = careerScore ? careerScore + careerMatch.matchScore : careerMatch.matchScore;

          careerScores.set(careerMatch.career, newCareerScore);
        });
      });

      // eslint-disable-next-line id-length
      const orderedCareerScores = [...careerScores].sort((a, b) => b[1] - a[1]).slice(0, 25);

      this.combinedCareers = orderedCareerScores.map((careerScore) => {
        if (careerScore && careerScore[0]) {
          // CareerMatch.career is the career iri; CareerMinimumRead.id is the bare id.
          const careerFullReadFound = allCareers.find((careerMinimumRead: Api.CareerMinimumRead) =>
            getIdFromIri(careerScore[0]) === careerMinimumRead.id ? careerMinimumRead : false,
          );

          if (careerFullReadFound) {
            return careerFullReadFound;
          }

          throw new Error(`no CareerFullRead found for the CareerMatch ${careerScore[0]}`);
        }

        throw new Error('CareerMatch has no value');
      });
    }

    // eslint-disable-next-line consistent-return
    return this.combinedCareers;
  }

  public async getCompareToMeScores(): Promise<CompareToMeScores | undefined> {
    if (this.compareToMeScores) {
      return this.compareToMeScores;
    }

    if (this.reportCareers.length > 0) {
      this.compareToMeScores = { intelligencesScores: undefined, skillsScores: undefined, aptitudesScores: undefined };

      const assessmentMinimumReads = await this.getAssessmentMinimumReads();

      this.setCompareToMeScoresForAssessment(
        ReportEngine.findLatestReportStatusAssessment(AssessmentTypeIri.INTELLIGENCES_ASSESSMENT_TYPE_IRI, assessmentMinimumReads),
        AssessmentTypeIri.INTELLIGENCES_ASSESSMENT_TYPE_IRI,
      );

      this.setCompareToMeScoresForAssessment(
        ReportEngine.findLatestReportStatusAssessment(AssessmentTypeIri.SKILLS_ASSESSMENT_TYPE_IRI, assessmentMinimumReads),
        AssessmentTypeIri.SKILLS_ASSESSMENT_TYPE_IRI,
      );

      // The aptitude parent (type 21) carries the macro scores once any macro is fully scored, even
      // though its own completedAt stays null until the whole battery is done.
      this.setCompareToMeScoresForAssessment(
        ReportEngine.findAptitudeParentWithMacroScores(assessmentMinimumReads),
        AssessmentTypeIri.APTITUDES_ASSESSMENT_TYPE_IRI,
      );

      // Runs last so the report's own assessment wins the type it shares with the latest report-status
      // assessment of that type - the same ordering getCareerMatchesByAssessmentType uses. Reading from
      // the already awaited assessmentMinimumReads keeps it synchronous, so there is no unawaited set
      // for the calls above to race.
      const reportAssessmentId = getIdFromIri(this.achieveWorksReport.assessment);

      this.setCompareToMeScoresForAssessment(
        assessmentMinimumReads.find((assessmentMinimumRead: Api.AssessmentMinimumRead) => assessmentMinimumRead.id === reportAssessmentId),
        this.assessmentTypeIri,
      );
    }

    return this.compareToMeScores;
  }

  public getToken(): string {
    return this.reportClient.getToken();
  }

  public getUser(): Api.UserFullRead {
    return this.achieveWorksReport.user;
  }

  public getProductConfiguration(): Api.ProductConfigurationFullRead {
    return this.achieveWorksReport.productConfiguration;
  }

  // eslint-disable-next-line class-methods-use-this
  public getStatus(): string {
    return this.status;
  }

  public async updateAssessmentRatingQuestion(ratingId: string): Promise<void> {
    await this.mutex.use(async () => {
      const { assessmentRating } = this.getReport();

      if (assessmentRating) {
        const result = await this.assessmentClient.updateAssessmentRatingQuestion(
          getIdFromIri(this.getReport().assessment),
          ratingId,
          assessmentRating.id,
        );

        this.getReport().assessmentRating = result;
        this.initializeRatingAnswers();
      } else {
        throw new Error('Assessment Rating answer missing');
      }
    });
  }

  // Explicit per type rather than an intelligences/else ternary: skills and aptitudes both read
  // macroScores, but aptitude macro scores arrive on a 0-1 scale and need normalizing. Types with no
  // facet-level compare data (personality, learning styles) fall through and set nothing.
  protected setCompareToMeScoresForAssessment(
    assessmentMinimumRead: Api.AssessmentMinimumRead | undefined,
    assessmentTypeIri: string,
  ): void {
    if (!assessmentMinimumRead || !this.compareToMeScores) {
      return;
    }

    // Assign only what the assessment actually carries: the report's own assessment is applied last
    // and must not blank a value the latest completed assessment of that type already supplied.
    switch (assessmentTypeIri) {
      case AssessmentTypeIri.INTELLIGENCES_ASSESSMENT_TYPE_IRI:
        this.compareToMeScores.intelligencesScores = assessmentMinimumRead.scores ?? this.compareToMeScores.intelligencesScores;
        break;
      case AssessmentTypeIri.SKILLS_ASSESSMENT_TYPE_IRI:
        this.compareToMeScores.skillsScores = assessmentMinimumRead.macroScores ?? this.compareToMeScores.skillsScores;
        break;
      case AssessmentTypeIri.APTITUDES_ASSESSMENT_TYPE_IRI:
        this.compareToMeScores.aptitudesScores =
          ReportEngine.normalizeAptitudeMacroScores(assessmentMinimumRead.macroScores) ?? this.compareToMeScores.aptitudesScores;
        break;
      default:
        break;
    }
  }

  // Aptitude user macro scores arrive on a 0-1 scale; the compare graphs and the overall average
  // expect 0-100 (like skills/intelligences), so normalize here at the SPA boundary.
  protected static normalizeAptitudeMacroScores(
    macroScores: Array<Api.AssessmentMacroScoreMinimumRead> | undefined,
  ): Array<Api.AssessmentMacroScoreMinimumRead> | undefined {
    return macroScores ? macroScores.map((macroScore) => ({ ...macroScore, score: macroScore.score * 100 })) : undefined;
  }

  // Gates on report status, not completedAt (PE-3168): a personality assessment sits in
  // RateAssessment with completedAt set and no career matches yet, so an abandoned retake would win
  // selection by date and hide the finished older assessment.
  //
  // updatedAt fallback because maxBy's iteratee is also its filter - it skips a candidate the
  // iteratee answers undefined for, so the gate alone would not keep it.
  protected static findLatestReportStatusAssessment(
    assessmentTypeIri: string,
    assessmentMinimumReads: Api.AssessmentMinimumRead[],
  ): Api.AssessmentMinimumRead | undefined {
    return assessmentMinimumReads
      ? // eslint-disable-next-line consistent-return
        maxBy(assessmentMinimumReads, (assessmentMinimumRead): string | undefined => {
          if (isReportStatus(assessmentMinimumRead.status) && assessmentTypeIri === assessmentMinimumRead.assessmentType) {
            return assessmentMinimumRead.completedAt ?? assessmentMinimumRead.updatedAt;
          }
        })
      : undefined;
  }

  // Aptitude career matches live on the parent assessment (type 21), whose completedAt is only set
  // once every section is done - so it cannot come from findLatestReportStatusAssessment. Gate on
  // the parent carrying a macro score rather than on a section being completed:
  // AptitudesParentAssessmentScoreCalculator::complete() creates career matches only when it adds a
  // macro score, so a parent with none has nothing to contribute, and its empty macroScores array
  // would otherwise be assigned downstream where an empty array reads as data.
  // Newest by createdAt, not first in API order: a user can hold more than one parent (a retake), and
  // a mid-battery parent has no completedAt to order by. The report's own assessment still wins its
  // type regardless of age - both callers apply it last, after this.
  protected static findAptitudeParentWithMacroScores(
    assessmentMinimumReads: Api.AssessmentMinimumRead[],
  ): Api.AssessmentMinimumRead | undefined {
    if (!assessmentMinimumReads) {
      return undefined;
    }

    const scoredAptitudeParents = assessmentMinimumReads.filter(
      (assessmentMinimumRead) =>
        assessmentMinimumRead.assessmentType === AssessmentTypeIri.APTITUDES_ASSESSMENT_TYPE_IRI &&
        (assessmentMinimumRead.macroScores?.length ?? 0) > 0,
    );

    // `?? scoredAptitudeParents[0]` because maxBy returns undefined when the iteratee is undefined for
    // every candidate: createdAt is in the read_assessment_minimum group today, but if it ever left,
    // ordering silently degrading to API order is survivable where losing aptitudes entirely is not.
    return maxBy(scoredAptitudeParents, (assessmentMinimumRead) => assessmentMinimumRead.createdAt) ?? scoredAptitudeParents[0];
  }

  // The date an aptitude compare row shows while the battery is unfinished. The parent's own
  // completedAt is only set once every macro type is scored, so the newest completed SECTION stands
  // in.
  //
  // The caller passes the parent whose row is being shown rather than this method selecting one: a
  // retake means the user holds more than one parent, and the row can belong to either - the report's
  // own assessment wins its type regardless of age, so the winner is only known to the caller that
  // built the row. Sections are matched by parentAssessment so a stalled battery never dates another.
  //
  // Lives here rather than in the caller because all three facts it needs are SDK knowledge: which
  // type iris are sections, that the parent's completedAt lags, and how sections link to a parent.
  public async findLatestCompletedAptitudeSectionDate(aptitudeParentId: string): Promise<string | null> {
    const assessmentMinimumReads = await this.getAssessmentMinimumReads();
    const latestCompletedSection = maxBy(
      assessmentMinimumReads.filter(
        (assessmentMinimumRead) =>
          AssessmentTypeIri.APTITUDE_SECTION_ASSESSMENTS.includes(assessmentMinimumRead.assessmentType) &&
          assessmentMinimumRead.completedAt != null &&
          assessmentMinimumRead.parentAssessment != null &&
          getIdFromIri(assessmentMinimumRead.parentAssessment) === aptitudeParentId,
      ),
      (assessmentMinimumRead) => assessmentMinimumRead.completedAt,
    );

    return latestCompletedSection?.completedAt ?? null;
  }
}
